import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { PageTitle } from '../components/PageTitle';
import { Plus, Trash2, Ban, Shield, Crown, User, Mail, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    subscription_plan: 'usuario'
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/admin/users`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Usuario creado exitosamente. Puede iniciar sesión inmediatamente.');
      setShowCreateModal(false);
      setFormData({ email: '', password: '', name: '', subscription_plan: 'usuario' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleUpgradePlan = async (userId, newPlan) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/admin/users/${userId}/subscription?plan=${newPlan}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Plan actualizado a ${newPlan}`);
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar plan');
    }
  };

  const handleBanUser = async (userId, currentBanned) => {
    const action = currentBanned ? 'desbanear' : 'banear';
    if (!window.confirm(`¿Estás seguro de ${action} este usuario?`)) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/admin/users/${userId}/ban`, {
        banned: !currentBanned,
        reason: !currentBanned ? 'Violación de términos de servicio' : null
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Usuario ${action}do correctamente`);
      fetchUsers();
    } catch (error) {
      toast.error(`Error al ${action} usuario`);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${userEmail}? Esta acción no se puede deshacer.`)) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Usuario eliminado correctamente');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div className="pl-12 md:pl-0">
            <PageTitle>Panel de Gestión de Usuarios</PageTitle>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-3 rounded-lg font-bold flex items-center gap-2 hover:scale-105 transition-transform shadow-lg"
          >
            <Plus size={20} />
            Crear Usuario
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-green-600 to-green-800 p-6 rounded-xl shadow-lg">
            <div className="flex items-center gap-3">
              <Crown className="text-yellow-300" size={32} />
              <div>
                <div className="text-white/80 text-sm">Profesional</div>
                <div className="text-white text-3xl font-bold">
                  {users.filter(u => u.subscription_plan === 'profesional' || u.subscription_plan === 'premium').length}
                </div>
                <div className="text-white/60 text-xs">€24.99/mes</div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg">
            <div className="flex items-center gap-3">
              <Shield className="text-white" size={32} />
              <div>
                <div className="text-white/80 text-sm">Avanzado</div>
                <div className="text-white text-3xl font-bold">
                  {users.filter(u => u.subscription_plan === 'usuario_avanzado' || u.subscription_plan === 'essential').length}
                </div>
                <div className="text-white/60 text-xs">€4.99/mes</div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-xl shadow-lg">
            <div className="flex items-center gap-3">
              <User className="text-white" size={32} />
              <div>
                <div className="text-white/80 text-sm">Usuario</div>
                <div className="text-white text-3xl font-bold">
                  {users.filter(u => !u.subscription_plan || u.subscription_plan === 'usuario' || u.subscription_plan === 'basic' || u.subscription_plan === 'free').length}
                </div>
                <div className="text-white/60 text-xs">GRATIS</div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-600 to-red-800 p-6 rounded-xl shadow-lg">
            <div className="flex items-center gap-3">
              <Ban className="text-white" size={32} />
              <div>
                <div className="text-white/80 text-sm">Baneados</div>
                <div className="text-white text-3xl font-bold">
                  {users.filter(u => u.banned).length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="text-white text-center py-12">Cargando usuarios...</div>
        ) : (
          <div className="bg-gray-900 rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-900 to-purple-800 text-white">
                    <th className="p-4 text-left">Usuario</th>
                    <th className="p-4 text-left">Email</th>
                    <th className="p-4 text-left">Plan</th>
                    <th className="p-4 text-left">Creado</th>
                    <th className="p-4 text-left">Estado</th>
                    <th className="p-4 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-white/10 hover:bg-white/5 transition">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                              {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                              {u.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                          )}
                          <div>
                            <div className="text-white font-semibold">{u.name}</div>
                            {u.is_admin && (
                              <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded font-bold">ADMIN</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-white/80">{u.email}</td>
                      <td className="p-4">
                        <select
                          value={u.subscription_plan}
                          onChange={(e) => handleUpgradePlan(u.user_id, e.target.value)}
                          disabled={u.is_admin}
                          className="bg-gray-800 text-white px-3 py-1 rounded border border-white/20 disabled:opacity-50"
                        >
                          <option value="usuario">Usuario (Gratis)</option>
                          <option value="usuario_avanzado">Avanzado (€4.99)</option>
                          <option value="profesional">Profesional (€24.99)</option>
                        </select>
                      </td>
                      <td className="p-4 text-white/60 text-sm">
                        {new Date(u.created_at).toLocaleDateString('es-ES')}
                      </td>
                      <td className="p-4">
                        {u.banned ? (
                          <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                            BANEADO
                          </span>
                        ) : (
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                            ACTIVO
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-center">
                          {!u.is_admin && (
                            <>
                              <button
                                onClick={() => handleBanUser(u.user_id, u.banned)}
                                className={`p-2 rounded hover:scale-110 transition ${
                                  u.banned ? 'bg-green-600' : 'bg-orange-600'
                                } text-white`}
                                title={u.banned ? 'Desbanear' : 'Banear'}
                              >
                                <Ban size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.user_id, u.email)}
                                className="p-2 rounded bg-red-600 text-white hover:scale-110 transition"
                                title="Eliminar"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl border border-white/20 p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-white mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Crear Nuevo Usuario
              </h2>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Nombre</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-3 bg-gray-800 border border-white/20 rounded text-white focus:border-purple-500 focus:outline-none"
                    placeholder="Nombre completo"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 bg-gray-800 border border-white/20 rounded text-white focus:border-purple-500 focus:outline-none"
                    placeholder="email@ejemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Contraseña</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full p-3 bg-gray-800 border border-white/20 rounded text-white focus:border-purple-500 focus:outline-none"
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Plan</label>
                  <select
                    value={formData.subscription_plan}
                    onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
                    className="w-full p-3 bg-gray-800 border border-white/20 rounded text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="usuario">Usuario (Gratis)</option>
                    <option value="usuario_avanzado">Avanzado (€4.99/mes)</option>
                    <option value="profesional">Profesional (€24.99/mes)</option>
                  </select>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-blue-300 text-sm">
                    ℹ️ El usuario podrá iniciar sesión inmediatamente con estas credenciales (sin verificación de email).
                  </p>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 text-black py-3 rounded-lg font-bold hover:scale-105 transition-transform"
                    style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}
                  >
                    Crear Usuario
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setFormData({ email: '', password: '', name: '', subscription_plan: 'usuario' });
                    }}
                    className="flex-1 bg-gray-700 text-white py-3 rounded-lg font-bold hover:bg-gray-600 transition"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
